import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, InputNumber, Select, Switch, Modal } from 'antd';
import { PROJECT_COLORS, CURRENCIES } from '../../_shared/constants';

const colorOptions = PROJECT_COLORS.map((c) => ({
  label: (
    <span className="flex items-center gap-2">
      <span
        className="inline-block h-3 w-3 rounded-full"
        style={{ backgroundColor: c }}
      />
      {c}
    </span>
  ),
  value: c,
}));

const currencyOptions = Object.entries(CURRENCIES).map(([key, val]) => ({
  label: key,
  value: val,
}));

export default function ProjectForm({
  open,
  onClose,
  onSubmit,
  initialValues = null,
  customers = [],
  isLoading = false,
}) {
  const { t } = useTranslation(['forms', 'tables']);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          name: initialValues.name,
          code: initialValues.code,
          client: initialValues.client?._id || initialValues.client,
          color: initialValues.color || '#1890ff',
          description: initialValues.description,
          billable: initialValues.billable || false,
          hourlyRate: initialValues.hourlyRate || 0,
          currency: initialValues.currency || 'rsd',
          budgetHours: initialValues.budgetHours,
          active: initialValues.status !== 'ARCHIVED',
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { active, ...rest } = values;
      onSubmit({ ...rest, status: active ? 'ACTIVE' : 'ARCHIVED' });
    } catch {
      // validation failed
    }
  };

  return (
    <Modal
      title={
        initialValues
          ? t('editProject', { ns: 'forms' })
          : t('newProject', { ns: 'forms' })
      }
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="btn-default" onClick={onClose}>
            {t('cancel', { ns: 'forms' })}
          </Button>
          <Button
            className="btn-primary"
            loading={isLoading}
            onClick={handleOk}
          >
            {t('save', { ns: 'forms' })}
          </Button>
        </div>
      }
      destroyOnClose
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          color: '#1890ff',
          billable: false,
          hourlyRate: 0,
          currency: 'rsd',
          active: true,
        }}
      >
        <Form.Item
          name="name"
          label={t('projectName', { ns: 'forms' })}
          rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="code" label={t('projectCode', { ns: 'forms' })}>
          <Input />
        </Form.Item>

        <Form.Item name="client" label={t('client', { ns: 'forms' })}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={customers.map((c) => ({ label: c.name, value: c._id }))}
            placeholder={t('selectClient', { ns: 'forms' })}
          />
        </Form.Item>

        <Form.Item name="color" label={t('color', { ns: 'forms' })}>
          <Select options={colorOptions} />
        </Form.Item>

        <Form.Item name="description" label={t('description', { ns: 'forms' })}>
          <Input.TextArea rows={3} />
        </Form.Item>

        <div className="flex gap-4">
          <Form.Item
            name="billable"
            label={t('billable', { ns: 'forms' })}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item name="hourlyRate" label={t('hourlyRate', { ns: 'forms' })}>
            <InputNumber min={0} className="w-full" />
          </Form.Item>

          <Form.Item name="currency" label={t('currency', { ns: 'forms' })}>
            <Select options={currencyOptions} className="w-24" />
          </Form.Item>
        </div>

        <Form.Item name="budgetHours" label={t('budgetHours', { ns: 'forms' })}>
          <InputNumber min={0} className="w-full" />
        </Form.Item>

        <Form.Item
          name="active"
          label={t('active', { ns: 'forms' })}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
